import React, { useEffect, useRef, useState } from "react";
import { Plus, Search, UserCheck, Lock, ExternalLink, X } from "lucide-react";
import Header from "../Common/AdminHeader";
import image from '../../../imgs/chatImage.png';
import { motion } from 'framer-motion';
import AcceptingTrainerCard from "../../../Shared components/AcceptingTrainerCard";
import axios from "axios";
import { Config } from "../../../API/config";
import toast from "react-hot-toast";

interface Admin {
    name: string;
    email: string;
    phone: string;
    password: string;
}

const ManageAdmins: React.FC = () => {
    const [admins, setAdmins] = useState<Admin[]>([]);
    const [showDialog, setShowDialog] = useState(false);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
    const [loading, setLoading] = useState(false);
    const [newAdmin, setNewAdmin] = useState<Admin>({ name: "", email: "", phone: "", password: "" });
    const dialogRef = useRef<HTMLDivElement>(null);
    const detailsDialogRef = useRef<HTMLDivElement>(null);

    const addAdmin = async () => {
        try {
            setLoading(true);
            let { data } = await axios.post(Config.addNewAdmin, newAdmin, { withCredentials: true });


            if (data.statusCode === 200) {
                toast.success("تمت اضافة مسؤول");
                await getAllAdmins();
                setNewAdmin({ name: "", email: "", phone: "", password: "" });
            }
        } catch (err) {
            console.error("Error adding admin:", err);
            toast.error("فشل في إضافة المسؤول");
        } finally {
            setLoading(false);
            setShowDialog(false);
        }
    };

    const getAllAdmins = async () => {
        try {
            setLoading(true);
            let { data } = await axios.get(Config.getAllAdmins, { withCredentials: true });


            if (data) {
                setAdmins(data)
            } else {
                setAdmins([]);
            }
        } catch (err) {
            console.error("Error fetching admins:", err);
            toast.error("فشل في جلب المسؤولين");
            setAdmins([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAllAdmins();
    }, []);

    // Handle click outside for add admin dialog
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
                setShowDialog(false);
            }
        };
        if (showDialog) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showDialog]);

    // Handle click outside for details dialog
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (detailsDialogRef.current && !detailsDialogRef.current.contains(event.target as Node)) {
                setShowDetailsDialog(false);
            }
        };
        if (showDetailsDialog) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showDetailsDialog]);

    const handleShowDetails = (admin: Admin) => {
        setSelectedAdmin(admin);
        setShowDetailsDialog(true);
    };

    return (
        <div className='flex-1 overflow-auto relative z-10 bg-white'>
            <Header title={"ادارة المسؤولون"} />

            <main className='max-w-7xl mx-auto py-6 px-4 lg:px-8'>
                <div className="bg-white border rounded-lg p-4 w-full max-w-3xl mx-auto">
                    <div className="flex justify-between items-center border-b pb-3">
                        <div className="flex items-center space-x-reverse space-x-3">
                            <button
                                onClick={() => setShowDialog(true)}
                                className="bg-green-800 text-white px-4 py-2 rounded-md flex items-center space-x-reverse space-x-2"
                                disabled={loading}
                            >
                                <Plus size={16} className="mr-4" />
                                <span>اضف مسؤول</span>
                            </button>
                        </div>
                        <div className="flex items-center space-x-reverse space-x-2">
                            <UserCheck size={20} className="text-gray-700" />
                            <h2 className="text-lg font-semibold text-gray-900">المسؤولون الحاليون</h2>
                        </div>
                    </div>

                    <div className="mt-4 space-y-4">
                        {loading ? (
                            <p className="text-center text-gray-500 py-4">جاري التحميل...</p>
                        ) : admins.length > 0 ? (
                            admins.map((admin, index) => (
                                <div key={index} className="flex flex-row-reverse items-center justify-between p-2 bg-gray-100 rounded-lg">
                                    <div className="flex flex-row-reverse items-center space-x-reverse space-x-3">
                                        <div className="text-right">
                                            <p className="text-gray-900 font-medium">{admin.name}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-reverse space-x-2">
                                        <ExternalLink
                                            size={18}
                                            className="text-gray-500 cursor-pointer hover:text-gray-700"
                                            onClick={() => handleShowDetails(admin)}
                                        />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-center text-gray-500 py-4">لا يوجد مسؤولين حاليًا</p>
                        )}
                    </div>
                </div>

                {/* Add Admin Dialog */}
                {showDialog && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center">
                        <div ref={dialogRef} className="bg-white p-6 rounded-lg shadow-lg w-96 relative">
                            <button className="absolute top-2 left-2" onClick={() => setShowDialog(false)} disabled={loading}>
                                <X size={20} />
                            </button>
                            <h3 className="text-lg font-semibold text-center mb-4">إضافة مسؤول جديد</h3>
                            <input
                                type="email"
                                placeholder="الإيميل"
                                className="w-full p-2 border rounded mb-2 text-right"
                                value={newAdmin.email}
                                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                                disabled={loading}
                            />
                            <input
                                type="text"
                                placeholder="الاسم"
                                className="w-full p-2 border rounded mb-2 text-right"
                                value={newAdmin.name}
                                onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                                disabled={loading}
                            />
                            <input
                                type="password"
                                placeholder="كلمة المرور"
                                className="w-full p-2 border rounded mb-2 text-right"
                                value={newAdmin.password}
                                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                                disabled={loading}
                            />
                            <input
                                type="text"
                                placeholder="رقم الهاتف"
                                className="w-full p-2 border rounded mb-2 text-right"
                                value={newAdmin.phone}
                                onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })}
                                disabled={loading}
                            />
                            <button
                                onClick={addAdmin}
                                className={`w-full px-4 py-2 rounded text-white ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-200 hover:bg-primary-300'}`}
                                disabled={loading}
                            >
                                {loading ? "جاري الإضافة..." : "إضافة"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Admin Details Dialog */}
                {showDetailsDialog && selectedAdmin && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center">
                        <div ref={detailsDialogRef} className="bg-white p-6 rounded-lg shadow-lg w-96 relative">
                            <button 
                                className="absolute top-2 left-2" 
                                onClick={() => setShowDetailsDialog(false)}
                            >
                                <X size={20} />
                            </button>
                            <h3 className="text-lg font-semibold text-center mb-4">تفاصيل المسؤول</h3>
                            <div className="space-y-3 text-right">
                                <p><span className="font-medium">الاسم:</span> {selectedAdmin.name}</p>
                                <p><span className="font-medium">الإيميل:</span> {selectedAdmin.email}</p>
                                <p><span className="font-medium">رقم الهاتف:</span> {selectedAdmin.phone}</p>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default ManageAdmins;